import { classUserSelectors, createAppSelector } from "./base";
import { selectIsClassSessionInPerson, selectIsClassSessionVirtual } from "./class-session";
import { selectSectionUserIdToUserIdMap } from "./section";
import { selectCurrentSectionUserId, selectSectionUserIdByUserId } from "./section-user";

import { ClassUserType } from "components/server-types";

export const selectClassUserBySectionUserId = createAppSelector(
  [(state, sectionUserId: string) => sectionUserId, classUserSelectors.filterForCurrentSection],
  (sectionUserId, classUsers): ClassUserType | undefined =>
    classUsers.find((cu) => cu.section_user_id === sectionUserId),
);

// Map of class user ids to user ids
// TODO: this could be optimized since the ids should not change that often
export const selectClassUserIdToUserIdMap = createAppSelector(
  [classUserSelectors.selectAll, selectSectionUserIdToUserIdMap],
  (classUsers, sectionUserIdToUserIdMap) => {
    const classUserIdToUserIdMap = new Map<string, string | null>();
    classUsers.forEach((classUser) => {
      classUserIdToUserIdMap.set(
        classUser.id,
        sectionUserIdToUserIdMap.get(classUser.section_user_id) || null,
      );
    });
    return classUserIdToUserIdMap;
  },
);

export const selectUserByClassUserId = createAppSelector(
  [
    selectClassUserIdToUserIdMap,
    (state) => state.users,
    (state, classUserId: string) => classUserId,
  ],
  (classUserIdToUserIdMap, users, classUserId) => {
    const userId = classUserIdToUserIdMap.get(classUserId) || null;
    return userId && userId in users ? users[userId] : null;
  },
);

export const selectUserIdByClassUserId = createAppSelector(
  [selectClassUserIdToUserIdMap, (state, classUserId: string) => classUserId],
  (classUserIdToUserIdMap, classUserId) => classUserIdToUserIdMap.get(classUserId) || null,
);

export const selectClassUserByUserId = createAppSelector(
  [classUserSelectors.selectAll, selectSectionUserIdByUserId],
  (classUsers, sectionUserId) => classUsers.find((cu) => cu.section_user_id === sectionUserId),
);

export const selectClassUserIdByUserId = createAppSelector(
  [selectClassUserByUserId],
  (classUser) => classUser?.id || null,
);

export const selectClassUserIsInVirtualClassByUserId = createAppSelector(
  [selectClassUserByUserId, selectIsClassSessionVirtual],
  (classUser, isClassSessionVirtual) =>
    isClassSessionVirtual && (classUser?.is_in_class_session || false),
);

export const selectCurrentClassUser = createAppSelector(
  [classUserSelectors.filterForCurrentSection, selectCurrentSectionUserId],
  (classUsers, currentSectionUserId): ClassUserType | undefined => {
    if (!classUsers || !currentSectionUserId) {
      return undefined;
    }
    // Find the class user that matches the current section user
    return classUsers.find((classUser) => classUser.section_user_id === currentSectionUserId);
  },
);

export const selectCurrentClassUserId = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.id || null,
);

export const selectCurrentClassUserReaction = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.reaction || null,
);

export const selectCurrentClassUserVote = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.vote || null,
);

export const selectIsHandRaisedByClassUserId = createAppSelector(
  [(state, classUserId: string) => classUserId, classUserSelectors.filterForCurrentSection],
  (classUserId, classUsers): boolean => {
    const classUser = classUsers.find((cu) => cu.id === classUserId);
    return !!classUser?.hand_raised_at;
  },
);

export const selectCurrentClassUserIsInPersonClass = createAppSelector(
  [selectCurrentClassUser, selectIsClassSessionInPerson],
  (classUser, isClassSessionInPerson) =>
    isClassSessionInPerson && (classUser?.is_in_class_session || false),
);

// NOTE: this is not the same is isInVirtualClass from useAv(), which takes into account
// the main AV tab and classUser.is_performing_av_check. Use this selector only if you
// don't need to check those conditions.
export const selectCurrentClassUserIsInVirtualClass = createAppSelector(
  [selectCurrentClassUser, selectIsClassSessionVirtual],
  (classUser, isClassSessionVirtual) =>
    isClassSessionVirtual && (classUser?.is_in_class_session || false),
);

export const selectCurrentClassUserIsInClassSession = createAppSelector(
  [selectCurrentClassUserIsInPersonClass, selectCurrentClassUserIsInVirtualClass],
  (isInPersonClass, isInVirtualClass) => isInPersonClass || isInVirtualClass,
);

export const selectCurrentClassUserBreakoutGroupId = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.breakout_group_id || null,
);

export const selectCurrentClassUserIsPerformingAvCheck = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.is_performing_av_check || false,
);
